import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, CheckIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiSend } from "react-icons/fi";
import { timesheetService, projectService } from "../services/edosService";
import { useNavigate, useSearchParams } from "react-router-dom";

const STATUS_COLORS = {
  draft: "gray",
  submitted: "blue",
  approved: "green",
  rejected: "red",
};

const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const candidates = [
    payload.entries,
    payload.rows,
    payload.items,
    payload.data,
    payload.projects,
  ];

  for (const value of candidates) {
    if (Array.isArray(value)) return value;
  }

  return [];
};

export default function TimesheetsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryProjectId, setSummaryProjectId] = useState(
    searchParams.get("projectId") || "",
  );
  const [tabIndex, setTabIndex] = useState(0);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(extractArray(res?.data));
    } catch (_) {}
  }, []);

  const fetchMyEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timesheetService.myEntries();
      setEntries(extractArray(res?.data?.data || res?.data));
    } catch (err) {
      toast({ title: "Failed to load timesheet entries", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchProjectSummary = useCallback(async () => {
    if (!summaryProjectId) return;
    setLoading(true);
    try {
      const res = await timesheetService.projectSummary(summaryProjectId);
      setSummary(res.data.data || res.data);
    } catch (err) {
      toast({ title: "Failed to load project summary", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [summaryProjectId, toast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (tabIndex === 0) fetchMyEntries();
    else if (tabIndex === 1) fetchProjectSummary();
  }, [tabIndex, fetchMyEntries, fetchProjectSummary]);

  const handleSubmit = async (id) => {
    try {
      await timesheetService.submit(id);
      toast({ title: "Entry submitted for approval", status: "success" });
      fetchMyEntries();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  const handleApprove = async (id) => {
    try {
      await timesheetService.approve(id);
      toast({ title: "Entry approved", status: "success" });
      fetchMyEntries();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  const minutesToHours = (m) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}h ${min}m`;
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Timesheets
          </Text>
          <Text color="gray.500" fontSize="sm">
            Log and track time across projects and tickets
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={tabIndex === 0 ? fetchMyEntries : fetchProjectSummary}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="orange"
            onClick={() =>
              navigate(
                `/edos/timesheets/create${summaryProjectId ? `?projectId=${summaryProjectId}` : ""}`,
              )
            }
          >
            Log Time
          </Button>
        </HStack>
      </Flex>

      <Tabs onChange={setTabIndex}>
        <TabList mb={4}>
          <Tab>My Entries</Tab>
          <Tab>Project Summary</Tab>
        </TabList>
        <TabPanels>
          {/* My Entries */}
          <TabPanel px={0}>
            <Box
              bg={bg}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              overflow="hidden"
            >
              {loading ? (
                <Flex justify="center" p={10}>
                  <Spinner size="xl" color="orange.500" />
                </Flex>
              ) : !Array.isArray(entries) || entries.length === 0 ? (
                <Alert status="info" m={4}>
                  <AlertIcon />
                  No time entries found.
                </Alert>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead bg={headerBg}>
                    <Tr>
                      <Th>Date</Th>
                      <Th>Duration</Th>
                      <Th>Project</Th>
                      <Th>Ticket</Th>
                      <Th>Billable</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {entries.map((e) => (
                      <Tr key={e._id}>
                        <Td>
                          <Text fontSize="sm">
                            {new Date(e.date).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {minutesToHours(e.totalMinutes)}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {e.projectId?.projectName || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {e.ticketId?.title || e.ticketId || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={e.billable ? "green" : "gray"}>
                            {e.billable ? "Yes" : "No"}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={STATUS_COLORS[e.status] || "gray"}
                          >
                            {e.status}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            {e.status === "draft" && (
                              <Tooltip label="Submit">
                                <IconButton
                                  size="xs"
                                  icon={<FiSend />}
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleSubmit(e._id)}
                                />
                              </Tooltip>
                            )}
                            {e.status === "submitted" && (
                              <Tooltip label="Approve">
                                <IconButton
                                  size="xs"
                                  icon={<CheckIcon />}
                                  variant="ghost"
                                  colorScheme="green"
                                  onClick={() => handleApprove(e._id)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </TabPanel>

          {/* Project Summary */}
          <TabPanel px={0}>
            <Flex gap={3} mb={4} align="center">
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                Select Project:
              </Text>
              <Select
                placeholder="Choose a project..."
                value={summaryProjectId}
                onChange={(e) => setSummaryProjectId(e.target.value)}
                maxW="320px"
              >
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName}
                  </option>
                ))}
              </Select>
            </Flex>
            {!summaryProjectId ? (
              <Alert status="info">
                <AlertIcon />
                Select a project to view its timesheet summary.
              </Alert>
            ) : loading ? (
              <Flex justify="center" p={10}>
                <Spinner />
              </Flex>
            ) : summary ? (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {Object.entries(summary)
                  .filter(([, v]) => typeof v !== "object")
                  .map(([k, v]) => (
                    <Box
                      key={k}
                      bg={bg}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={4}
                    >
                      <Stat>
                        <StatLabel color="gray.500" textTransform="capitalize">
                          {k.replace(/([A-Z])/g, " $1")}
                        </StatLabel>
                        <StatNumber fontSize="lg">
                          {typeof v === "number" &&
                          k.toLowerCase().includes("minute")
                            ? minutesToHours(v)
                            : String(v)}
                        </StatNumber>
                      </Stat>
                    </Box>
                  ))}
              </SimpleGrid>
            ) : null}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
